import React, { useContext } from "react";
import PropTypes from "prop-types";
import Headline from "~/assets/js/components/Typography/Headline";
import MarkdownText from "~/assets/js/components/Typography/MarkdownText";
import { ForwardButton } from "~/assets/js/components/Button";
import i18n from "~/assets/js/Helpers/i18n";
import {
    ACTIONS,
    ITEM_TYPE,
    UPDATE_TYPE,
    ProductsDetailsContext,
} from "../../ProductsProvider";
import AddonCard from "~/assets/js/components/AddonCard";
import AdditionCard from "~/assets/js/components/AddonCard/AdditionCard";
import { formatPrice } from "~/assets/js/Format";
import { DeliveryContext } from "~/assets/js/models/DeliveryContext";
import { calculateFranchisePrice } from "~/assets/js/Helpers/util";

const DesktopBasicIngredients = ({
    setExtrasOpened,
    ingredientsDescription,
}) => {
    const {
        state,
        updateProductState,
        isBowl,
        isSalad,
        isDiscoverMode,
        triggerAddressPopup,
    } = useContext(ProductsDetailsContext);
    const { storeId } = useContext(DeliveryContext);

    const { basic } = state;

    const incrementIngredient = (product_id) => {
        if (isDiscoverMode) {
            triggerAddressPopup();
            return;
        }

        const data = {
            product_id,
            update: UPDATE_TYPE.INCREMENT,
            itemType: ITEM_TYPE.BASIC,
        };
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const decrementIngredient = (product_id, quantity) => {
        if (isDiscoverMode) {
            triggerAddressPopup();
            return;
        }

        const data = {
            product_id,
            itemType: ITEM_TYPE.BASIC,
        };

        if (quantity === 1) {
            return updateProductState({ type: ACTIONS.REMOVE, payload: data });
        }

        data.update = UPDATE_TYPE.DECREMENT;
        return updateProductState({ type: ACTIONS.UPDATE, payload: data });
    };

    const calculateDisplayPrice = (ingredient) => {
        const price = calculateFranchisePrice(storeId, ingredient);

        if (ingredient.quantity > 1) {
            return formatPrice(price * (ingredient.quantity - 1));
        }

        return i18n.t("basicIngredients.includesOne", {
            productName: isBowl
                ? i18n.t("basicIngredients.inBowl")
                : isSalad
                ? i18n.t("basicIngredients.inSalad")
                : i18n.t("basicIngredients.inSoup"),
        });
    };

    return (
        <div id="basic-ingredients">
            <div className="header">
                <Headline size={5} resizable className="title">
                    {i18n.t("basicIngredients.title")}
                </Headline>
                <ForwardButton
                    size="XS"
                    variant="secondary"
                    label={i18n.t("basicIngredients.extras")}
                    onClick={
                        isDiscoverMode ? triggerAddressPopup : setExtrasOpened
                    }
                />
            </div>
            <MarkdownText>{ingredientsDescription}</MarkdownText>
            <div className="ingredients-wrapper">
                {basic.map((ingredient) => {
                    const displayPrice = calculateDisplayPrice(ingredient);

                    const ingredientProps = {
                        name: ingredient.name,
                        image: ingredient.images.normal,
                        description: displayPrice,
                        details: ingredient.details,
                        quantity: ingredient.quantity,
                        tags: ingredient.tags,
                        increment: () =>
                            incrementIngredient(ingredient.product_id),
                        decrement: () =>
                            decrementIngredient(
                                ingredient.product_id,
                                ingredient.quantity
                            ),
                    };
                    return (
                        <AddonCard
                            key={ingredient.product_id}
                            {...ingredientProps}
                        />
                    );
                })}
                <AdditionCard
                    onClick={
                        isDiscoverMode ? triggerAddressPopup : setExtrasOpened
                    }
                />
            </div>
        </div>
    );
};

DesktopBasicIngredients.propTypes = {
    setExtrasOpened: PropTypes.func,
    ingredientsDescription: PropTypes.string,
};

export default DesktopBasicIngredients;
